import React from "react";
import { PropertyValuationData } from "../../../../types/valuation";
import { CompGallery } from "../CompGallery";
import { formatCurrency } from "../../../../utils/formatters";
import { Bed, Bath, Ruler } from "lucide-react";
import { Card } from "../../../../components/shared/Card/Card";

interface ComparablePropertiesProps {
  propertyData: PropertyValuationData;
  colors: {
    text: string;
    primary: string;
  };
  transformValue: (value: any) => number;
}

export const ComparableProperties: React.FC<ComparablePropertiesProps> = ({
  propertyData,
  colors,
  transformValue,
}) => {
  if (!propertyData.comps || propertyData.comps.length === 0) {
    return null;
  }

  return (
    <Card title="Comparable Properties" className="mb-8" elevation="md">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {propertyData.comps.slice(0, 6).map((comp, index) => (
          <div
            key={index}
            className="rounded-xl overflow-hidden transform transition-all hover:scale-[1.02] bg-white border-2 border-gray-20"
          >
            <CompGallery comp={comp} />
            <div className="p-4">
              <div className="space-y-1 lg:space-y-2">
                {/* Price */}
                <div className="flex items-center gap-4">
                  <div className="text-black !font-bold text-lg lg:text-xl">
                    {comp.mlsSoldPrice
                      ? formatCurrency(transformValue(comp.mlsSoldPrice))
                      : formatCurrency(transformValue(comp.estimatedValue))}
                  </div>
                  <span
                    className="px-2 py-1 rounded-full text-white text-[11px] lg:text-xs"
                    style={{
                      backgroundColor: comp.mlsSoldPrice
                        ? "#ef4444"
                        : "#4b5563",
                    }}
                  >
                    {comp.mlsSoldPrice ? "Sold" : "Estimated"}
                  </span>
                </div>

                {/* Address */}
                <p className="!font-semibold text-gray-900 line-clamp-1 text-sm lg:text-[17px]">
                  {comp.address?.address}
                </p>

                {/* Property Details */}
                <div className="flex items-center gap-4 text-xs lg:text-sm text-gray-700">
                  <div className="flex items-center gap-1">
                    <div className="p-1">
                      <Bed
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    </div>
                    <span className="font-medium">{comp.bedrooms || "?"}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="p-1">
                      <Bath
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    </div>
                    <span className="font-medium">{comp.bathrooms || "?"}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="p-1">
                      <Ruler
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    </div>
                    <span className="font-medium">
                      {transformValue(comp.squareFeet).toLocaleString()}
                    </span>
                    <span className="text-gray-500">sf</span>
                  </div>
                </div>

                {/* Additional Details */}
                <div className="pt-2 text-[11px] lg:text-xs text-gray-500">
                  <hr className="mb-3" />
                  <p>
                    Built {comp.yearBuilt}
                    {comp.mlsDaysOnMarket && (
                      <>
                        <span className="mx-2">•</span>
                        {comp.mlsDaysOnMarket} days on market
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
